import React, { CSSProperties, ReactElement, useState } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'
import LoadingBar from './LoadingBar'
import Spinner from './Spinner'
import { Chevron } from '../icons/Chevron'
import { ListTableSurface } from './DashboardPrimitives'

const TableContainer = styled.div<{ numColumns: number, borderless?: boolean, mergeHeader?: boolean }>`
  ${({ borderless }) => !borderless && `
    background: var(--surface, #ffffff);
    border-radius: 14px;
    border: 1px solid var(--border, #e9eef5);
    overflow: hidden;
  `};
  width: 100%;
  border-spacing: 0;
  display: grid;
  grid-template-columns: ${({ numColumns }) => `repeat(${numColumns}, minmax(100px, auto));`}
`

const TableHeader = styled.div<{ sortable: boolean, borderless?: boolean }>`
  border-bottom: 1px solid;
  border-color: var(--border, #e7ecf3);
  background-color: var(--surface-subtle, #fafbfd);
  gap: 10px;
  padding: 11px 16px;
  text-align: left;
  display: flex;
  align-items: center;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-soft, #7d8492);
  position: sticky;
  top: 0;
  z-index: 1;

  ${({ sortable }) => sortable && `
    cursor: pointer;
    user-select: none;
  `}

  &:first-child {
    border-top-left-radius: ${({ borderless }) => borderless ? '0' : '14px'};
  }

  &:last-child {
    border-top-right-radius: ${({ borderless }) => borderless ? '0' : '14px'};
  }
`

const TableRow = styled.div<{ $clickable?: boolean, $disableHoverStyle?: boolean }>`
  cursor: ${({ $clickable, $disableHoverStyle }) => $disableHoverStyle ? 'default' : $clickable ? 'pointer' : 'default'};
  display: contents;
  color: ${RADOM_COLORS.BLACK};

  ${({ $clickable }) => $clickable && `
    &:active {
      > div {
        opacity: 0.5;
      }
    }
  `}

  ${({ $disableHoverStyle }) => $disableHoverStyle
    ? `
      cursor: initial !important;
    `
    : `
      &:hover {
        > div {
          background-color: var(--surface-subtle, #fafbfd);
        }
      }
  `}

  &:nth-last-child(2) {
      > div {
        border-bottom-left-radius: 14px;
        border-bottom-right-radius: 14px;
        border-bottom: 0;
    }
  }
`

const TableCell = styled.div`
  color: var(--ink, #20242d);
  font-size: 13px;
  line-height: 1.5;
  padding: 12px 16px;
  border-bottom: 1px solid;
  border-color: var(--surface-pressed, #edf1f7);
  overflow-wrap: anywhere;
  display: flex;
  align-items: center;
`

interface HeaderItem {
  key: string
  element: ReactElement
  sortable?: boolean
}

function isHeaderItem(header: ReactElement | HeaderItem): header is HeaderItem {
  return 'element' in (header as HeaderItem)
}

interface IProps {
  headers: Array<ReactElement | HeaderItem>
  items: ReactElement[][]
  isLoading: boolean
  emptyPlaceholderText?: string
  onClickRow?: (i: number) => void
  isRowClickable?: (i: number) => boolean
  onSortChange?: (sorts: Sorts) => void
  disableOnClickState?: boolean
  disableHoverStyle?: boolean
  tableContainerStyle?: CSSProperties
  borderless?: boolean
  initialSorts?: Sorts
  mergeHeader?: boolean
}

export interface Sorts {
  [headerKey: string]: SortType
}

export enum SortType {
  None,
  Descending,
  Ascending
}

function nextSortType(sortType?: SortType): SortType {
  if (sortType === undefined) {
    return SortType.Descending
  }

  switch (sortType) {
  case SortType.None:
    return SortType.Descending
  case SortType.Descending:
    return SortType.Ascending
  case SortType.Ascending:
    return SortType.Descending
  }
}

function rotationFromSortType(sortType?: SortType): number {
  switch (sortType) {
  case SortType.Descending:
    return 0
  case SortType.Ascending:
    return 180
  }

  return 0
}

const Table = (props: IProps): React.ReactElement => {
  const rowRefs: HTMLDivElement[] = []

  const [sorts, setSorts] = useState<Sorts>(props.initialSorts ?? {})

  const handleRowClick = (i: number): void => {
    if (props.isRowClickable && !props.isRowClickable(i)) {
      return
    }
    if (props.onClickRow) {
      props.onClickRow(i)
    }
  }

  return <ListTableSurface style={props.mergeHeader ? { border: 'none', borderRadius: 0, background: 'transparent', overflow: 'visible' } : {}}>

    <TableContainer
      mergeHeader={props.mergeHeader}
      borderless={props.borderless}
      style={props.tableContainerStyle} numColumns={props.headers.length || 1}>
      {
        props.headers.map((header, index) => {
          const headerItem = isHeaderItem(header)
            ? header
            : {
              key: `${index}`,
              element: header,
              sortable: false
            }

          return (
            <TableHeader

              borderless={props.borderless}
              key={index}
              sortable={!!headerItem.sortable}
              onClick={() => {
                if (headerItem.sortable) {
                  const newSorts = {
                    [headerItem.key]: nextSortType(sorts[headerItem.key])
                  }
                  setSorts(newSorts)
                  props.onSortChange?.(newSorts)
                }
              }}>
              <span>
                {headerItem.element}
              </span>
              {
                headerItem.sortable &&
            <Chevron style={{
              opacity: !sorts[headerItem.key] ? 0.25 : 1,
              transition: '0.2s ease all',
              transform: `rotate(${rotationFromSortType(sorts[headerItem.key])}deg)`
            }} />
              }
            </TableHeader>
          )
        })
      }

      {
        !props.isLoading && props.items.length <= 0 &&
        <div style={{ gridColumn: `span ${props.headers.length}` }}>
          <div style={{ padding: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 13, color: RADOM_COLORS.GRAY_DARKER }}>{props.emptyPlaceholderText}</span>
          </div>
        </div>
      }

      {
        props.isLoading && props.items.length <= 0 &&
        Array.from(Array(8)).map(() => props.headers.map((_, i) =>
          <div key={i} style={{ padding: 12 }}>
            <LoadingBar style={{ height: 28, width: '100%' }} />
          </div>
        ))
      }

      {
        props.items.map((tableRow, index) =>
          <TableRow
            key={index}
            ref={ref => { if (ref) rowRefs[index] = ref }}
            $clickable={
              !!props.onClickRow &&
            !props.disableOnClickState &&
            (!props.isRowClickable || props.isRowClickable(index))
            }
            $disableHoverStyle={!!props.disableHoverStyle}>
            {
              tableRow.map((item, i) =>
                <TableCell key={i} onClick={e => {
                  if (rowRefs[index].contains(e.target as any)) {
                    handleRowClick(index)
                  }
                }}>
                  {item}
                </TableCell>
              )
            }
          </TableRow>
        )
      }

      {
        <div style={{
          gridColumn: `span ${props.headers.length}`,
          height: props.isLoading && props.items.length > 0 ? 50 : 0,
          overflow: 'hidden',
          borderTop: props.isLoading && props.items.length > 0 ? `1px solid ${RADOM_COLORS.GRAY_DARK}` : 0,
          transition: '0.5s ease height'
        }}>
          <div style={{ padding: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner />
          </div>
        </div>
      }
    </TableContainer>
  </ListTableSurface>
}

export default Table
